import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DataExplorerPeriodSelector} from '../../data_explorer/DataExplorerPeriodSelector';
import {AccountsScopeSelector} from './AccountsScopeSelector';
import {ComparePeriodSelector} from './ComparePeriodSelector';
import {FiltersRow} from './FiltersRow';
import {periodToSelectorState, selectorStateToPeriod} from './periodState';
import {ACCOUNTS_FLASH_MS, ACCOUNTS_PILL_MS} from '../state/clientSwitch';
import {anchorAttr} from '../../../guides/anchors';

export {periodToSelectorState, selectorStateToPeriod};

/**
 * La barra de AJUSTES (card con acento rojo): modo cuentas / líneas de control, selección,
 * período, comparar con, y la fila de filtros. Todo lo que está acá es del informe (y del
 * cliente), nada de la plantilla.
 */
export function SettingsBar({settings, formData, agencyClientId, accountsResetAt, onChange, onClearAccountsReset, selectorKey = 'new'}) {
    const {t} = useTranslation();
    const [flash, setFlash] = useState(false);
    const [pill, setPill] = useState(false);

    useEffect(() => {
        if (!accountsResetAt) return undefined;
        setFlash(true);
        setPill(true);
        const flashTimer = setTimeout(() => setFlash(false), ACCOUNTS_FLASH_MS);
        const pillTimer = setTimeout(() => { setPill(false); if (onClearAccountsReset) onClearAccountsReset(); }, ACCOUNTS_PILL_MS);
        return () => { clearTimeout(flashTimer); clearTimeout(pillTimer); };
    }, [accountsResetAt, onClearAccountsReset]);

    const periodState = useMemo(() => periodToSelectorState(settings.period), [settings.period]);
    // El selector se remonta cuando cambia el INFORME (`selectorKey`), no cuando cambia el período:
    // su `componentDidMount` dispara `onChange` siempre, y remontarlo por período era el loop.

    return (
        <div className="inf-settings" data-testid="informes-settings" {...anchorAttr('informes_settings')}>
            <span className="inf-settings-eyebrow">{t('informes.settings.eyebrow', 'AJUSTES')}</span>
            {pill && <span className="inf-accounts-reset-pill">{t('informes.settings.accounts_reset', 'Cuentas restablecidas')}</span>}
            <div className="inf-settings-row">
                <AccountsScopeSelector
                    sourceType={settings.source_type}
                    agencyClientId={agencyClientId}
                    businessUnits={formData ? formData.businessUnits : []}
                    adAccounts={formData ? formData.adAccounts : []}
                    selectedItems={settings.selected_items || []}
                    onChange={(items) => onChange({selected_items: items})}
                    onSourceTypeChange={(sourceType) => onChange({source_type: sourceType, selected_items: []})}
                    flash={flash}
                />
                <DataExplorerPeriodSelector
                    key={selectorKey}
                    initialPeriodState={periodState}
                    onChange={(state) => {
                        const period = selectorStateToPeriod(state);
                        if (period && JSON.stringify(period) !== JSON.stringify(settings.period)) onChange({period});
                    }}
                />
                <label className="inf-compare-toggle">
                    {/* El input queda oculto y el `.inf-sw` dibuja el interruptor (mockup). */}
                    <input type="checkbox" checked={!!settings.compare}
                           onChange={(e) => onChange({
                               compare: e.target.checked,
                               compare_period: e.target.checked
                                   ? (settings.compare_period || {type: 'RELATIVE', predefined_period: 'PREVIOUS_PERIOD'})
                                   : settings.compare_period,
                           })}/>
                    <span className="inf-sw" aria-hidden="true"/>
                    <span>{t('data_explorer.compare_with', 'Comparar con')}</span>
                </label>
                {/* Con la comparación apagada el select no se muestra: ocupaba una fila entera,
                    se podía cambiar y no hacía nada (QA UI-05). */}
                {settings.compare && (
                    <ComparePeriodSelector
                        value={settings.compare_period}
                        onChange={(compare_period) => onChange({compare_period})}
                    />
                )}
            </div>
            {formData && formData.fieldsData && (
                <FiltersRow
                    fieldsData={formData.fieldsData}
                    sourceType={settings.source_type}
                    filters={settings.dimensions_filters}
                    onChange={(dimensions_filters) => onChange({dimensions_filters})}
                />
            )}
        </div>
    );
}
